import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthState, UserRole } from '../domain';
import { isManageSubdomain } from '@/api/core/authContext';

// Estado inicial simplificado enfocado en UI y Cookies
const initialState: AuthState = {
  user: null,
  role: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

// Tipo para el store simplificado
type AuthStoreContext = AuthState & {
  // Helpers de roles
  isAdmin: () => boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;

  // Acciones puras de estado
  setState: (newState: Partial<AuthState>) => void;
  resetState: () => void;
  logout: () => void;
};

// Determinar la clave de storage correcta usando isManageSubdomain()
// Esto soporta: /admin/*, /dashboard/*, /login?admin=true, manage.* subdomain
const getStorageKey = () =>
  isManageSubdomain() ? 'auth-storage-admin' : 'auth-storage-user';

// Crear el store con persistencia
export const useAuthStore = create<AuthStoreContext>()(
  persist(
    (set, get) => ({
      ...initialState,

      isAdmin: () => {
        const role = get().role;
        return role === 'admin';
      },

      hasRole: (role: UserRole) => {
        return get().role === role;
      },

      hasAnyRole: (roles: UserRole[]) => {
        const userRole = get().role;
        return userRole ? roles.includes(userRole) : false;
      },

      setState: (newState) => set((state) => ({ ...state, ...newState })),
      resetState: () => set(initialState),
      logout: () => set(initialState),
    }),
    {
      name: getStorageKey(), // Clave correcta independientemente de la ruta actual
      storage: createJSONStorage(() => localStorage),
      // Solo persistimos banderas visuales, NUNCA tokens JWT aquí por seguridad XSS.
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        role: state.role, // roles básicos para UI
        // user: state.user - Opcional: Solo si no contiene datos sensibles
      }),
    }
  )
);

// Sincronización Multi-Pestaña Segura (Cross-tab Synchronization)
// Escucha cambios en el localStorage hechos por ESTA MISMA APP en OTRAS pestañas
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    const storageKey = isManageSubdomain() ? 'auth-storage-admin' : 'auth-storage-user';

    // Si el evento viene de nuestra llave de autenticación
    if (event.key === storageKey && event.newValue) {
      try {
        const newState = JSON.parse(event.newValue);
        // Si otra pestaña cerró sesión (isAuthenticated: false) o limpió el estado
        if (newState?.state?.isAuthenticated === false) {
          useAuthStore.getState().resetState();
        } else if (newState?.state?.isAuthenticated === true) {
          // Opcional: Sincronizar login. Por ahora solo sincronizamos el LOGOUT por seguridad.
          // Para sincronizar LOGIN, requeriría hacer re-fetch del usuario para obtener el Token seguro en Cookie.
        }
      } catch (e) {
        console.error('Error procesando sincronización de auth entre pestañas', e);
      }
    } else if (event.key === storageKey && !event.newValue) {
      // Alguien borró completamente la llave del storage (Logout fuerte)
      useAuthStore.getState().resetState();
    }
  });
}
